import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { nextTick, onUnmounted, ref, watch } from 'vue';

import { useWebSocketStore } from '@/stores/useWebSocketStore';

export interface DispatchDashboardViewWSComposable {
  updatedDispatchJob: Readonly<
    Ref<ReadonlyDeep<openapi.DespatchJobsContentOpenapi[0]> | undefined>
  >;
  createdDispatchJob: Readonly<
    Ref<ReadonlyDeep<openapi.DespatchJobsContentOpenapi[0]> | undefined>
  >;
  lockedDispatchJobId: Readonly<Ref<number>>;
  unlockedDispatchJobId: Readonly<Ref<number>>;
  yellowMarkedDispatchDriverId: Readonly<Ref<number>>;
  redMarkedDispatchDriverId: Readonly<Ref<number>>;
  greenMarkedDispatchDriverId: Readonly<Ref<number>>;
  unmarkedDispatchDriverId: Readonly<Ref<number>>;
}

export default function useDispatchDashboardViewWS(): DispatchDashboardViewWSComposable {
  const updatedDispatchJob = ref<openapi.DespatchJobsContentOpenapi[0]>();
  const createdDispatchJob = ref<openapi.DespatchJobsContentOpenapi[0]>();
  const lockedDispatchJobId = ref<number>(NaN);
  const unlockedDispatchJobId = ref<number>(NaN);
  const yellowMarkedDispatchDriverId = ref<number>(NaN);
  const redMarkedDispatchDriverId = ref<number>(NaN);
  const greenMarkedDispatchDriverId = ref<number>(NaN);
  const unmarkedDispatchDriverId = ref<number>(NaN);

  const webSocketStore = useWebSocketStore();

  const webSocket = webSocketStore.getWebSocket();

  watch(
    () => webSocketStore.message,
    async function (newValues, oldValues) {
      // console.log(webSocketStore.message);
      // payload.job_id is not jobId
      if (webSocketStore.message.payload.job_id) {
        // TODO maybe array for lock/unlock jobs?
        if (webSocketStore.message.message_type === 'dispatch.update_job') {
          updatedDispatchJob.value = undefined;
          await nextTick(function () {
            updatedDispatchJob.value = webSocketStore.message.payload.data;
          });
        } else if (webSocketStore.message.message_type === 'dispatch.new_job') {
          createdDispatchJob.value = undefined;
          await nextTick(function () {
            createdDispatchJob.value = webSocketStore.message.payload.data;
          });
        } else if (webSocketStore.message.message_type === 'dispatch.lock') {
          lockedDispatchJobId.value = NaN;
          await nextTick(function () {
            lockedDispatchJobId.value = webSocketStore.message.payload.job_id; // payload.job_id is not jobId
          });
        } else if (webSocketStore.message.message_type === 'dispatch.unlock') {
          unlockedDispatchJobId.value = NaN;
          await nextTick(function () {
            unlockedDispatchJobId.value = webSocketStore.message.payload.job_id; // payload.job_id is not jobId
          });
        }
      } else if (webSocketStore.message.payload.driver_id) {
        // TODO what are they `driver.id`, `driver.driver`, `driver_id`, `driver.code`, `driverCode`, `driver_is`?
        // TODO for all cases "reason": "Test reason for yellow marking"
        // TODO driver_id is string in debug endpoint response, but it is number in ws response
        if (
          webSocketStore.message.message_type === 'dispatch.mark_driver_yellow'
        ) {
          yellowMarkedDispatchDriverId.value = NaN;
          await nextTick(function () {
            yellowMarkedDispatchDriverId.value =
              webSocketStore.message.payload.driver_id;
          });
        } else if (
          webSocketStore.message.message_type === 'dispatch.mark_driver_red'
        ) {
          redMarkedDispatchDriverId.value = NaN;
          await nextTick(function () {
            redMarkedDispatchDriverId.value =
              webSocketStore.message.payload.driver_id;
          });
        } else if (
          webSocketStore.message.message_type === 'dispatch.mark_driver_green'
        ) {
          greenMarkedDispatchDriverId.value = NaN;
          await nextTick(function () {
            greenMarkedDispatchDriverId.value =
              webSocketStore.message.payload.driver_id;
          });
        } else if (
          webSocketStore.message.message_type === 'dispatch.unmark_driver'
        ) {
          unmarkedDispatchDriverId.value = NaN;
          await nextTick(function () {
            unmarkedDispatchDriverId.value =
              webSocketStore.message.payload.driver_id;
          });
        }
      }
    },
  );

  watch(
    () => webSocketStore.isAuth,
    async function (newValues, oldValues) {
      if (webSocketStore.isAuth) {
        const msg = {
          type: 'subscribe',
          channel: 'dispatch',
        };
        webSocket.value?.send(JSON.stringify(msg));
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    const msg = {
      type: 'unsubscribe',
      channel: 'dispatch',
    };
    webSocket.value?.send(JSON.stringify(msg));
  });

  return {
    updatedDispatchJob,
    createdDispatchJob,
    lockedDispatchJobId,
    unlockedDispatchJobId,
    yellowMarkedDispatchDriverId,
    redMarkedDispatchDriverId,
    greenMarkedDispatchDriverId,
    unmarkedDispatchDriverId,
  };
}
